/**
 * The pages that move the rank (SYR-111 v4).
 *
 * Within one assistant, the answers that read a page are set against the
 * answers that did not: the brand's rank score in each, and how often it was
 * listed. Only pages clear of noise are shown (the 90% interval excludes no
 * difference). Association, not cause: pages arrive together, so the copy
 * says "when it read", never "because".
 */

import React from 'react';
import { useIntl } from 'react-intl';
import { Tag } from 'antd';
import { Card, Eyebrow } from '../../atoms';
import { THEME } from '../../config/theme';
import { IModelBeliefsPageMove, IModelBeliefsPageMoves } from '../../../../services/nestjs-base-api';
import { InfoTip } from './InfoTip';
import { usePageKindLabel, usePageLabel } from './pageKind';
import { fmt } from './beliefCells';

const UP = '#059669';
const DOWN = '#dc2626';

const pct = (fraction: number): string => `${Math.round(fraction * 100)}%`;

interface Props {
  pageMoves: IModelBeliefsPageMoves[];
  brandName: string;
}

export const PagesThatMoveRank: React.FunctionComponent<Props> = ({ pageMoves, brandName }) => {
  const intl = useIntl();
  const kindLabel = usePageKindLabel();
  const pageLabel = usePageLabel();
  if (!pageMoves?.length) return null;

  const numbers = (move: IModelBeliefsPageMove, colour: string) =>
    intl.formatMessage(
      {
        id: 'marketerView.modelBeliefs.pageMoves.line',
        defaultMessage:
          'rank score {present} when it read the page, {absent} when it did not; listed {listedPresent} against {listedAbsent}; read in {n} answers',
      },
      {
        present: <strong style={{ color: colour }}>{fmt(move.scorePresent)}</strong>,
        absent: fmt(move.scoreAbsent),
        listedPresent: pct(move.listedPresent),
        listedAbsent: pct(move.listedAbsent),
        n: move.nPresent,
      },
    );

  // The same by kind of page: "lower when it reads review sites".
  const kindLine = (move: IModelBeliefsPageMove, colour: string, direction: 'higher' | 'lower') => (
    <div
      key={`kind-${move.domain}`}
      style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 8, padding: '6px 0' }}
    >
      <span style={{ fontWeight: 600, color: colour, fontSize: 13.5 }}>
        {intl.formatMessage(
          {
            id:
              direction === 'higher'
                ? 'marketerView.modelBeliefs.pageMoves.kindHigher'
                : 'marketerView.modelBeliefs.pageMoves.kindLower',
            defaultMessage:
              direction === 'higher' ? 'Higher when it reads {kind}' : 'Lower when it reads {kind}',
          },
          { kind: kindLabel(move.domain) },
        )}
      </span>
      <span style={{ fontSize: 13, color: THEME.textSecondary, minWidth: 0 }}>{numbers(move, colour)}</span>
    </div>
  );

  const line = (move: IModelBeliefsPageMove, colour: string) => (
    <div
      key={move.domain}
      style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 8, padding: '6px 0' }}
    >
      <span style={{ fontWeight: 600, color: THEME.textBase, fontSize: 13.5 }}>{move.domain}</span>
      <Tag style={{ margin: 0, fontSize: 11 }}>{pageLabel(move.kind, move.site)}</Tag>
      <span style={{ fontSize: 13, color: THEME.textSecondary, minWidth: 0 }}>
        {numbers(move, colour)}
      </span>
    </div>
  );

  return (
    <Card>
      <Eyebrow style={{ marginBottom: 6 }}>
        <InfoTip
          title={intl.formatMessage({
            id: 'marketerView.modelBeliefs.pageMoves.tip',
            defaultMessage:
              'For each assistant, its answers that read a page are compared with its answers that did not. A page is shown only when the difference is clear of chance and it was read in at least ten answers and missed in at least ten. Rank score runs from 7 (named first) to 1 (not listed). Pages arrive together, so this shows which pages go with a higher or lower rank, not that a page caused it.',
          })}
        >
          {intl.formatMessage({
            id: 'marketerView.modelBeliefs.pageMoves.title',
            defaultMessage: 'Pages that move your rank',
          })}
        </InfoTip>
      </Eyebrow>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 22 }}>
        {pageMoves.map((model) => {
          const brand = model.targets.find((t) => t.isBrand);
          const rivals = model.targets.filter((t) => !t.isBrand && t.helps.length > 0);
          const nothing =
            !brand ||
            (brand.helps.length === 0 &&
              brand.hurts.length === 0 &&
              (brand.kindHelps?.length ?? 0) === 0 &&
              (brand.kindHurts?.length ?? 0) === 0);
          return (
            <div key={`${model.provider}:${model.model}`} style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 700, color: THEME.textBase, fontSize: 14, marginBottom: 2 }}>
                {model.label}
              </div>
              <div style={{ fontSize: 12, color: THEME.textMuted, marginBottom: 8 }}>
                {intl.formatMessage(
                  {
                    id: 'marketerView.modelBeliefs.pageMoves.answers',
                    defaultMessage: '{n} answers with web search',
                  },
                  { n: model.answers },
                )}
              </div>
              {nothing && (
                <div style={{ fontSize: 13, color: THEME.textSecondary }}>
                  {intl.formatMessage(
                    {
                      id: 'marketerView.modelBeliefs.pageMoves.none',
                      defaultMessage: 'No page clearly moves where {brand} ranks.',
                    },
                    { brand: brandName },
                  )}
                </div>
              )}
              {brand && brand.helps.length > 0 && (
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: UP, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                    {intl.formatMessage({
                      id: 'marketerView.modelBeliefs.pageMoves.higher',
                      defaultMessage: 'You rank higher when it reads',
                    })}
                  </div>
                  {brand.helps.map((m) => line(m, UP))}
                </div>
              )}
              {brand && brand.hurts.length > 0 && (
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: DOWN, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                    {intl.formatMessage({
                      id: 'marketerView.modelBeliefs.pageMoves.lower',
                      defaultMessage: 'You rank lower when it reads',
                    })}
                  </div>
                  {brand.hurts.map((m) => line(m, DOWN))}
                </div>
              )}
              {brand && ((brand.kindHelps?.length ?? 0) > 0 || (brand.kindHurts?.length ?? 0) > 0) && (
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: THEME.textSecondary, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                    {intl.formatMessage({
                      id: 'marketerView.modelBeliefs.pageMoves.byKind',
                      defaultMessage: 'By kind of page',
                    })}
                  </div>
                  {(brand.kindHelps ?? []).map((m) => kindLine(m, UP, 'higher'))}
                  {(brand.kindHurts ?? []).map((m) => kindLine(m, DOWN, 'lower'))}
                </div>
              )}
              {rivals.length > 0 && (
                <div style={{ borderTop: `1px solid ${THEME.borderSubtle}`, paddingTop: 8, marginTop: 4 }}>
                  <div style={{ fontSize: 12, color: THEME.textSecondary, marginBottom: 2 }}>
                    {intl.formatMessage({
                      id: 'marketerView.modelBeliefs.pageMoves.rivals',
                      defaultMessage: 'Pages that lift a competitor',
                    })}
                  </div>
                  {rivals.map((rival) => (
                    <div key={rival.name} style={{ fontSize: 13, color: THEME.textSecondary, padding: '2px 0' }}>
                      <strong style={{ color: THEME.textBase }}>{rival.name}</strong>
                      {': '}
                      {rival.helps
                        .map((m) => `${m.domain} (${fmt(m.scorePresent)} / ${fmt(m.scoreAbsent)})`)
                        .join(', ')}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
};
